/**
 * Inbound brand requests: the shared vocabulary.
 *
 * Imported by the public marketing form, the CRM screen and the server, for
 * the same reason `pipeline.js` is — the budget bands are rendered as radio
 * options on a page anyone can reach and validated again on the way in, and
 * two copies of that list is how a form starts offering a value the API
 * rejects.
 */

export const BUDGET_BAND = {
  UNDER_5K: '0_5k',
  MID_10K: '5k_10k',
  MID_100K: '10k_100k',
  OVER_100K: '100k_plus',
};

/**
 * Ordered low to high, and that order is load-bearing: it is the sort key for
 * "biggest first" on the CRM screen, so a new band must be inserted in the
 * right place rather than appended.
 */
export const BUDGET_BANDS = [
  {
    value: BUDGET_BAND.UNDER_5K,
    label: '$0 – $5k',
    short: '0–5k',
    /** Used only for sorting and the "pipeline value" figure. Deliberately the
     *  bottom of the band: a guess that flatters us is a guess that makes the
     *  number useless. */
    floor: 0,
  },
  { value: BUDGET_BAND.MID_10K, label: '$5k – $10k', short: '5–10k', floor: 5_000 },
  { value: BUDGET_BAND.MID_100K, label: '$10k – $100k', short: '10–100k', floor: 10_000 },
  { value: BUDGET_BAND.OVER_100K, label: '$100k+', short: '100k+', floor: 100_000 },
];

export const BUDGET_BAND_VALUES = BUDGET_BANDS.map((band) => band.value);

export function budgetBandMeta(value) {
  return BUDGET_BANDS.find((band) => band.value === value) ?? null;
}

/**
 * Triage states.
 *
 * `new` → someone has to look. `reviewing` → someone is looking, so nobody
 * else needs to. `won` / `passed` → the decision, kept forever.
 *
 * There is no "deleted": the whole point of the screen is being able to answer
 * "which brands did we choose to work with, and which did we turn down", and
 * that question is only answerable if the ones we turned down are still there.
 */
export const REQUEST_STATUS = {
  NEW: 'new',
  REVIEWING: 'reviewing',
  WON: 'won',
  PASSED: 'passed',
};

export const REQUEST_STATUSES = [
  {
    value: REQUEST_STATUS.NEW,
    label: 'New',
    description: 'Nobody has looked at this yet.',
    tone: 'sky',
  },
  {
    value: REQUEST_STATUS.REVIEWING,
    label: 'Reviewing',
    description: 'Someone on the team has picked it up.',
    tone: 'amber',
  },
  {
    value: REQUEST_STATUS.WON,
    label: 'Working with',
    description: 'We took the project on.',
    tone: 'emerald',
  },
  {
    value: REQUEST_STATUS.PASSED,
    label: 'Passed',
    description: 'We turned it down. Kept on purpose.',
    tone: 'zinc',
  },
];

export const REQUEST_STATUS_VALUES = REQUEST_STATUSES.map((status) => status.value);

export function requestStatusMeta(value) {
  return REQUEST_STATUSES.find((status) => status.value === value) ?? null;
}

/** The two states that mean "still needs a human". */
export const OPEN_STATUSES = [REQUEST_STATUS.NEW, REQUEST_STATUS.REVIEWING];
